import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  FormControlLabel,
  Switch,
  Checkbox,
  CircularProgress,
  Snackbar
} from '@mui/material'
import { API_BASE_URL } from '../config/api'
import {
  Visibility,
  Edit,
  Gavel,
  Schedule,
  PlayArrow,
  Stop,
  CheckCircle,
  Person,
  LocationOn,
  AttachMoney,
  Add,
  Delete
} from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import ImageOverlay from '../components/ImageOverlay'

interface Lot {
  id?: string
  lot_name: string
  lot_description?: string
  quantity: number
  unit: string
  reserve_price: number
  quality_grade?: string
  lot_images?: any[]
  harvest_images?: any[]
}

interface AuctionLot {
  id: string
  lot_name: string
  lot_description?: string
  farmer_name?: string
  farmer_id: string
  quantity: number
  unit: string
  reserve_price: number
  starting_price: number
  current_bid: number
  bid_count: number
  auction_type: 'normal' | 'dutch'
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'cancelled' | 'pending_approval' | 'inactive' | 'pending'
  start_time: string
  end_time: string
  location?: string
  created_date: string
  winner_id?: string
  winner_name?: string
  quality_grade?: string
  auction_start_date?: string
  auction_end_date?: string
  auction_name?: string
  description?: string
  lots?: Lot[]
  product_name?: string
  lot_images?: any[]
  harvest_images?: any[]
  all_lot_images?: any[]
}

interface CreateAuctionData {
  auction_name: string
  description: string
  auction_type: 'normal' | 'dutch'
  start_time: Date | null
  duration_minutes: number
  auto_start: boolean
  starting_price: number
  reserve_price: number
}

const Auctions: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [auctions, setAuctions] = useState<AuctionLot[]>([])
  const [loading, setLoading] = useState(true)
  const [createDialog, setCreateDialog] = useState(false)
  const [editDialog, setEditDialog] = useState(false)
  const [actionDialog, setActionDialog] = useState(false)
  const [selectedAuction, setSelectedAuction] = useState<AuctionLot | null>(null)
  const [actionType, setActionType] = useState<'start' | 'stop' | 'cancel' | 'delete' | 'enable' | 'disable'>('start')
  const [viewAuctionDialog, setViewAuctionDialog] = useState(false)
  const [imageOverlayOpen, setImageOverlayOpen] = useState(false)
  const [overlayImages, setOverlayImages] = useState<string[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '', severity: 'info' })

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity })
  }
  
  const [createData, setCreateData] = useState<CreateAuctionData>({
    auction_name: '',
    description: '',
    auction_type: 'normal',
    start_time: null,
    duration_minutes: 60,
    auto_start: false,
    starting_price: 0,
    reserve_price: 0
  })

  const [editData, setEditData] = useState<CreateAuctionData>({
    auction_name: '',
    description: '',
    auction_type: 'normal',
    start_time: null,
    duration_minutes: 60,
    auto_start: false,
    starting_price: 0,
    reserve_price: 0
  })

  // Lot selection states
  const [addLotDialog, setAddLotDialog] = useState(false)
  const [availableLots, setAvailableLots] = useState<AuctionLot[]>([])
  const [loadingLots, setLoadingLots] = useState(false)
  const [selectedLotIds, setSelectedLotIds] = useState<string[]>([])

  const fetchAuctions = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      // Fetch ALL auction lots from the database
      const response = await fetch(`${API_BASE_URL}/admin/auctions/all`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch auctions: ${response.status}`)
      }

      const result = await response.json()
      const auctionData = result.data || result.auctions || []
      
      console.log('Fetched auctions:', auctionData)
      console.log('Sample auction data:', auctionData[0])
      
      // Debug image fields in the first auction
      if (auctionData.length > 0) {
        console.log('First auction image fields:', {
          id: auctionData[0].id,
          lot_name: auctionData[0].lot_name,
          lot_images: auctionData[0].lot_images,
          harvest_images: auctionData[0].harvest_images,
          images: auctionData[0].images,
          all_lot_images: auctionData[0].all_lot_images
        });
      }

      // Show all auction lots (admin-created auctions + approved farmer lots)
      const transformedAuctions: AuctionLot[] = [];
      for (const auction of auctionData) {
        // Parse images if stored as JSON string
        let lotImages = auction.lot_images || auction.images || [];
        let harvestImages = auction.harvest_images || [];
        let allLotImages = auction.all_lot_images || [];
        
        // Note: Auctions are stored directly in auction_lots table, not as separate lots
        // Images should be in the auction.lot_images or auction.images field
        
        console.log(`Auction ${auction.id} raw image data:`, {
          lotImages: auction.lot_images,
          harvestImages: auction.harvest_images,
          images: auction.images,
          allLotImages: auction.all_lot_images,
          lotImagesType: typeof auction.lot_images,
          imagesType: typeof auction.images
        });
        
        console.log(`Auction ${auction.id} detailed images:`, {
          'auction.lot_images': auction.lot_images,
          'auction.images': auction.images,
          'typeof lot_images': typeof auction.lot_images,
          'typeof images': typeof auction.images,
          'lot_images length': Array.isArray(auction.lot_images) ? auction.lot_images.length : 'not array',
          'images length': Array.isArray(auction.images) ? auction.images.length : 'not array'
        });
        
        if (typeof lotImages === 'string') {
          try {
            lotImages = JSON.parse(lotImages);
            console.log(`Auction ${auction.id} parsed lotImages:`, lotImages);
          } catch (e) {
            console.log(`Auction ${auction.id} failed to parse lotImages:`, auction.lot_images);
            lotImages = [];
          }
        }
        
        if (typeof harvestImages === 'string') {
          try {
            harvestImages = JSON.parse(harvestImages);
          } catch (e) {
            harvestImages = [];
          }
        }
        
        if (typeof allLotImages === 'string') {
          try {
            allLotImages = JSON.parse(allLotImages);
          } catch (e) {
            allLotImages = [];
          }
        }
        
        const transformed = {
          id: auction.id,
          lot_name: auction.lot_name || `Auction #${auction.id}`,
          product_name: auction.lot_description?.split('-')[0]?.trim() || 'Unknown Product',
          farmer_name: auction.farmer?.name || auction.farmer_name || 'Admin Created',
          farmer_id: auction.farmer_id,
          category: 'General',
          variety: auction.lot_description?.split('-')[1]?.split('(')[0]?.trim() || 'Standard',
          grade: auction.quality_grade || 'Standard',
          quantity: parseFloat(auction.quantity) || 0,
          unit: auction.unit || 'kg',
          reserve_price: parseFloat(auction.reserve_price) || 0,
          starting_price: parseFloat(auction.starting_price) || 0,
          current_bid: parseFloat(auction.current_bid) || 0,
          bid_count: auction.bid_count || 0,
          auction_type: auction.auction_type || 'normal',
          status: auction.status || 'draft',
          start_time: auction.auction_start_date || new Date().toISOString(),
          end_time: auction.auction_end_date || new Date().toISOString(),
          auction_start_date: auction.auction_start_date,
          auction_end_date: auction.auction_end_date,
          location: auction.inspection_location || 'Unknown Location',
          created_date: auction.created_at || new Date().toISOString(),
          winner_id: auction.winner_id,
          winner_name: auction.winner?.name,
          auction_name: auction.auction_name,
          description: auction.description,
          lot_images: lotImages,
          harvest_images: harvestImages,
          all_lot_images: allLotImages
        };
        
        console.log('Transformed auction:', transformed.id, 'starting_price:', transformed.starting_price, 'reserve_price:', transformed.reserve_price)
        transformedAuctions.push(transformed);
      }

      setAuctions(transformedAuctions)
    } catch (error) {
      console.error('Error fetching auctions:', error)
      setAuctions([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableLots = async () => {
    try {
      setLoadingLots(true)
      const token = localStorage.getItem('token')
      
      const response = await fetch(`${API_BASE_URL}/admin/auctions/available-lots`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      setAvailableLots(result.data || [])
    } catch (error) {
      console.error('Error fetching available lots:', error)
      setAvailableLots([])
    } finally {
      setLoadingLots(false)
    }
  }

  useEffect(() => {
    fetchAuctions()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'scheduled': return 'info'
      case 'completed': return 'primary'
      case 'cancelled': return 'error'
      case 'draft': return 'default'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <PlayArrow />
      case 'scheduled': return <Schedule />
      case 'completed': return <CheckCircle />
      case 'cancelled': return <Stop />
      case 'draft': return <Edit />
      default: return <Schedule />
    }
  }

  const handleOpenImageOverlay = (auction: AuctionLot, startIndex: number = 0) => {
    const images = [
      ...(auction.lot_images || []),
      ...(auction.harvest_images || []),
      ...(auction.all_lot_images || [])
    ].map((img: any) => {
      const imageUrl = typeof img === 'string' ? img : img.url
      return imageUrl?.startsWith('http') ? imageUrl : `${API_BASE_URL.replace('/api/v1', '')}${imageUrl}`
    })
    
    setOverlayImages(images)
    setCurrentImageIndex(startIndex)
    setImageOverlayOpen(true)
  }

  const handleViewAuction = (auction: AuctionLot) => {
    setSelectedAuction(auction)
    setViewAuctionDialog(true)
  }

  const handleCreateAuction = async () => {
    if (createData.auction_name && selectedLotIds.length > 0) {
      try {
        const token = localStorage.getItem('token')
        
        // Get only the selected lots
        const selectedLots = availableLots.filter(lot => selectedLotIds.includes(lot.id))
        
        // Calculate total quantity from selected lots only
        const totalQuantity = selectedLots.reduce((sum, lot) => sum + parseFloat(String(lot.quantity)), 0)
        const primaryUnit = selectedLots[0]?.unit || 'kg'
        const averageReservePrice = selectedLots.reduce((sum, lot) => sum + parseFloat(String(lot.reserve_price)), 0) / selectedLots.length
        
        // Use admin-set prices, fallback to calculated values if not set
        const reservePrice = createData.reserve_price > 0 ? createData.reserve_price : averageReservePrice
        const startingPrice = createData.starting_price > 0 ? createData.starting_price : (reservePrice * 1.5)
        
        // Create auction data with selected lot IDs
        const auctionData = {
          auction_name: createData.auction_name,
          description: createData.description || `Auction with ${selectedLotIds.length} lot(s)`,
          auction_type: createData.auction_type,
          auction_start_date: createData.start_time?.toISOString() || new Date().toISOString(),
          auction_end_date: new Date(
            (createData.start_time || new Date()).getTime() + 
            createData.duration_minutes * 60 * 1000
          ).toISOString(),
          lot_ids: selectedLotIds, // Send the IDs of selected lots
          total_quantity: totalQuantity,
          primary_unit: primaryUnit,
          reserve_price: reservePrice,
          starting_price: startingPrice
        }

        const response = await fetch(`${API_BASE_URL}/admin/auctions/create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(auctionData)
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `Failed to create auction: ${response.status}`)
        }

        const result = await response.json()
        console.log('Auction created successfully:', result)
        
        // Refresh auctions list
        await fetchAuctions()
        
        showNotification('Auction created successfully with selected lots!', 'success')
        setCreateDialog(false)
        
        // Reset form and selections
        setCreateData({
          auction_name: '',
          description: '',
          auction_type: 'normal',
          start_time: null,
          duration_minutes: 60,
          auto_start: false,
          starting_price: 0,
          reserve_price: 0
        })
        setSelectedLotIds([])
        
      } catch (error: any) {
        console.error('Error creating auction:', error)
        showNotification(`Failed to create auction: ${error.message}`, 'error')
      }
    } else {
      showNotification('Please provide auction name and select at least one lot', 'warning')
    }
  }

  const handleAuctionAction = async () => {
    if (selectedAuction) {
      if (actionType === 'delete') {
        await handleDeleteAuction()
        return
      }

      try {
        const token = localStorage.getItem('token')
        let endpoint = ''
        let method = 'PUT'
        
        switch (actionType) {
          case 'start':
          case 'enable':
            endpoint = `${API_BASE_URL}/admin/auctions/${selectedAuction.id}/activate`
            break
          case 'stop':
          case 'disable':
            endpoint = `${API_BASE_URL}/admin/auctions/${selectedAuction.id}/deactivate`
            break
          case 'cancel':
            endpoint = `${API_BASE_URL}/admin/auctions/${selectedAuction.id}/cancel`
            break
        }

        const response = await fetch(endpoint, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        await fetchAuctions()
        showNotification(`Auction ${actionType}d successfully!`, 'success')
        
      } catch (error) {
        console.error('Error updating auction:', error)
        showNotification(`Failed to ${actionType} auction. Please try again.`, 'error')
      }
      
      setActionDialog(false)
      setSelectedAuction(null)
    }
  }

  const handleEditAuction = async () => {
    if (selectedAuction && editData.auction_name) {
      try {
        console.log('=== HANDLE EDIT AUCTION ===');
        console.log('editData.start_time (Date object):', editData.start_time);
        console.log('editData.start_time type:', typeof editData.start_time);
        console.log('editData.start_time ISO:', editData.start_time?.toISOString());
        console.log('editData.duration_minutes:', editData.duration_minutes);
        
        const token = localStorage.getItem('token')
        
        const updateData = {
          lot_name: editData.auction_name,
          lot_description: editData.description,
          auction_type: editData.auction_type,
          auction_start_date: editData.start_time?.toISOString(),
          auction_end_date: editData.start_time ? new Date(
            editData.start_time.getTime() + editData.duration_minutes * 60 * 1000
          ).toISOString() : undefined,
          // Add missing fields that backend expects for proper persistence
          quantity: selectedAuction.quantity,
          unit: selectedAuction.unit,
          starting_price: editData.starting_price,
          reserve_price: editData.reserve_price
        }

        console.log('Sending update data:', JSON.stringify(updateData, null, 2));
        console.log('===========================');

        const response = await fetch(`${API_BASE_URL}/admin/auctions/${selectedAuction.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `Failed to update auction: ${response.status}`)
        }

        await fetchAuctions()
        showNotification('Auction updated successfully!', 'success')
        setEditDialog(false)
        setSelectedAuction(null)
        
      } catch (error: any) {
        console.error('Error updating auction:', error)
        showNotification(`Failed to update auction: ${error.message}`, 'error')
      }
    }
  }

  const handleDeleteAuction = async () => {
    if (selectedAuction) {
      try {
        const token = localStorage.getItem('token')

        const response = await fetch(`${API_BASE_URL}/admin/auctions/${selectedAuction.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to delete auction')
        }

        await fetchAuctions()
        showNotification('Auction deleted successfully!', 'success')
        setActionDialog(false)
        setSelectedAuction(null)
      } catch (error: any) {
        console.error('Error deleting auction:', error)
        showNotification(`Failed to delete auction: ${error.message}`, 'error')
      }
    }
  }

  const openEditDialog = (auction: AuctionLot) => {
    setSelectedAuction(auction)
    // Use auction_start_date and auction_end_date from database, fallback to start_time/end_time
    const startDate = auction.auction_start_date || auction.start_time;
    const endDate = auction.auction_end_date || auction.end_time;
    
    setEditData({
      auction_name: auction.auction_name || auction.lot_name || '',
      description: auction.description || '',
      auction_type: auction.auction_type || 'normal',
      start_time: new Date(startDate),
      duration_minutes: Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / (60 * 1000)),
      auto_start: false,
      starting_price: auction.starting_price || 0,
      reserve_price: auction.reserve_price || 0
    })
    setEditDialog(true)
  }

  const handleSelectLot = (lot: AuctionLot) => {
    if (selectedLotIds.includes(lot.id)) {
      setSelectedLotIds(prev => prev.filter(id => id !== lot.id))
    } else {
      setSelectedLotIds(prev => [...prev, lot.id])
    }
    
    // Calculate default prices when lots selection changes
    setTimeout(() => calculateDefaultPrices(), 100)
  }


  const openSelectLotsDialog = () => {
    fetchAvailableLots()
    setAddLotDialog(true)
  }

  const getTotalQuantity = () => {
    const selectedLots = availableLots.filter(lot => selectedLotIds.includes(lot.id))
    const total = selectedLots.reduce((sum, lot) => {
      const quantity = parseFloat(String(lot.quantity)) || 0
      return sum + quantity
    }, 0)
    return Number(total) || 0
  }

  const getTotalValue = () => {
    const selectedLots = availableLots.filter(lot => selectedLotIds.includes(lot.id))
    const total = selectedLots.reduce((sum, lot) => {
      const quantity = parseFloat(String(lot.quantity)) || 0
      const price = parseFloat(String(lot.reserve_price)) || 0
      return sum + (quantity * price)
    }, 0)
    return Number(total) || 0
  }

  const calculateDefaultPrices = () => {
    if (selectedLotIds.length === 0) return
    
    const selectedLots = availableLots.filter(lot => selectedLotIds.includes(lot.id))
    const averageReservePrice = selectedLots.reduce((sum, lot) => sum + parseFloat(String(lot.reserve_price)), 0) / selectedLots.length
    
    // Only update if prices are currently 0 (not manually set by admin)
    setCreateData(prev => ({
      ...prev,
      reserve_price: prev.reserve_price === 0 ? averageReservePrice : prev.reserve_price,
      starting_price: prev.starting_price === 0 ? (averageReservePrice * 1.5) : prev.starting_price
    }))
  }

  const filteredAuctions = auctions.filter(auction => {
    switch (selectedTab) {
      case 0: return true // All
      case 1: return auction.status === 'active'
      case 2: return auction.status === 'scheduled'
      case 3: return auction.status === 'completed'
      default: return true
    }
  })

  const stats = {
    total: auctions.length,
    active: auctions.filter(a => a.status === 'active').length,
    scheduled: auctions.filter(a => a.status === 'scheduled').length,
    completed: auctions.filter(a => a.status === 'completed').length,
    totalValue: auctions.reduce((sum, a) => sum + (a.current_bid * a.quantity), 0)
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Auction Management</Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Auction Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage auctions for crop and animal lots
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialog(true)}
          >
            Create Auction
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Gavel color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{stats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Auctions
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PlayArrow color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{stats.active}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Auctions
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Schedule color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">{stats.scheduled}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AttachMoney color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography variant="h4">${stats.totalValue.toLocaleString()}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Bid Value
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for filtering */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
            <Tab label={`All (${stats.total})`} />
            <Tab label={`Active (${stats.active})`} />
            <Tab label={`Scheduled (${stats.scheduled})`} />
            <Tab label={`Completed (${stats.completed})`} />
          </Tabs>
        </Box>

        {/* Auctions Table */}
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Auction Lot</TableCell>
                    <TableCell>Images</TableCell>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Product Details</TableCell>
                    <TableCell>Bidding</TableCell>
                    <TableCell>Timing</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAuctions.map((auction) => (
                    <TableRow key={auction.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {auction.lot_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {auction.product_name}
                          </Typography>
                          <Box mt={0.5}>
                            <Chip 
                              label={auction.auction_type}
                              size="small"
                              color={auction.auction_type === 'normal' ? 'primary' : 'secondary'}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {(auction.lot_images?.[0] || auction.harvest_images?.[0] || auction.all_lot_images?.[0]) ? (
                            <Box
                              component="img"
                              src={(() => {
                                const firstImage = auction.lot_images?.[0] || auction.harvest_images?.[0] || auction.all_lot_images?.[0];
                                if (!firstImage) return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f3f4f6" width="60" height="60"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                                const imageUrl = typeof firstImage === 'object' && firstImage.url ? firstImage.url : firstImage;
                                return imageUrl?.startsWith('http') ? imageUrl : `${API_BASE_URL.replace('/api/v1', '')}${imageUrl}`;
                              })()}
                              alt={auction.lot_name}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid #ddd',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 }
                              }}
                              onClick={() => handleOpenImageOverlay(auction, 0)}
                              onError={(e: any) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40"%3E%3Crect fill="%23f3f4f6" width="40" height="40"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="8"%3ENo Image%3C/text%3E%3C/svg%3E'
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                bgcolor: 'primary.light',
                                borderRadius: 1,
                                border: '1px solid #ddd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  opacity: 0.1
                                }}
                              />
                              <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Typography variant="caption" color="primary" fontWeight="bold" sx={{ fontSize: '8px', textAlign: 'center', lineHeight: 1.2 }}>
                                  {auction.quantity ? `${auction.quantity} ${auction.unit}` : 'No Image'}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {(auction.lot_images?.length || auction.harvest_images?.length || auction.all_lot_images?.length || 0)} images
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {auction.farmer_name}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <LocationOn fontSize="small" color="disabled" />
                              <Typography variant="caption" color="text.secondary">
                                {auction.location}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {auction.quantity} {auction.unit}
                        </Typography>
                        {auction.quality_grade && (
                          <Typography variant="caption" color="text.secondary">
                            Grade: {auction.quality_grade}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          Reserve: ${auction.reserve_price}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          ${auction.current_bid}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {auction.bid_count} bids
                        </Typography>
                        {auction.winner_name && (
                          <Typography variant="caption" display="block" color="primary.main">
                            Winner: {auction.winner_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Start: {new Date(auction.start_time).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          End: {new Date(auction.end_time).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(auction.status)}
                          label={auction.status}
                          color={getStatusColor(auction.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewAuction(auction)}
                          title="View Details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(auction)}
                          title="Edit Auction"
                        >
                          <Edit />
                        </IconButton>
                        {(auction.status === 'draft' || auction.status === 'scheduled') && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setSelectedAuction(auction)
                              setActionType('enable')
                              setActionDialog(true)
                            }}
                            title="Enable Auction"
                          >
                            <PlayArrow />
                          </IconButton>
                        )}
                        {auction.status === 'active' && (
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setSelectedAuction(auction)
                              setActionType('disable')
                              setActionDialog(true)
                            }}
                            title="Disable Auction"
                          >
                            <Stop />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedAuction(auction)
                            setActionType('delete')
                            setActionDialog(true)
                          }}
                          title="Delete Auction"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Create Auction Dialog */}
        <Dialog 
          open={createDialog} 
          onClose={() => setCreateDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New Auction</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Auction Name"
                    value={createData.auction_name}
                    onChange={(e) => setCreateData(prev => ({ ...prev, auction_name: e.target.value }))}
                    fullWidth
                    placeholder="e.g., Weekly Vegetable Auction #12"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={createData.description}
                    onChange={(e) => setCreateData(prev => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Brief description of the auction"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Selected Lots ({selectedLotIds.length})</Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={openSelectLotsDialog}
                        size="small"
                      >
                        Select Lots
                      </Button>
                    </Box>
                    
                    {selectedLotIds.length === 0 ? (
                      <Alert severity="info">No lots selected yet. Click "Select Lots" to choose from available farmer lots.</Alert>
                    ) : (
                      <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="primary.contrastText">
                          <strong>{selectedLotIds.length} lot(s) selected</strong>
                        </Typography>
                        <Typography variant="body2" color="primary.contrastText">
                          <strong>Total Quantity:</strong> {getTotalQuantity().toFixed(2)} kg
                        </Typography>
                        <Typography variant="body2" color="primary.contrastText">
                          <strong>Total Value:</strong> ${getTotalValue().toFixed(2)}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setSelectedLotIds([])}
                          sx={{ mt: 1, color: 'primary.contrastText' }}
                        >
                          Clear Selection
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Auction Type"
                    value={createData.auction_type}
                    onChange={(e) => setCreateData(prev => ({ ...prev, auction_type: e.target.value as 'normal' | 'dutch' }))}
                    fullWidth
                  >
                    <MenuItem value="normal">Normal Auction (Price increases)</MenuItem>
                    <MenuItem value="dutch">Dutch Auction (Price decreases)</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Reserve Price ($)"
                    type="number"
                    value={createData.reserve_price}
                    onChange={(e) => setCreateData(prev => ({ ...prev, reserve_price: Number(e.target.value) }))}
                    fullWidth
                    helperText="Minimum acceptable price (calculated from lots, but editable)"
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ color: 'text.secondary', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Starting Price ($)"
                    type="number"
                    value={createData.starting_price}
                    onChange={(e) => setCreateData(prev => ({ ...prev, starting_price: Number(e.target.value) }))}
                    fullWidth
                    helperText="Initial auction price (default = 150% of reserve price, but editable)"
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ color: 'text.secondary', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Duration (minutes)"
                    type="number"
                    value={createData.duration_minutes}
                    onChange={(e) => setCreateData(prev => ({ ...prev, duration_minutes: Number(e.target.value) }))}
                    fullWidth
                    helperText="e.g., 10 for 10 minutes, 60 for 1 hour, 1440 for 24 hours"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createData.auto_start}
                        onChange={(e) => setCreateData(prev => ({ ...prev, auto_start: e.target.checked }))}
                      />
                    }
                    label="Start auction immediately"
                  />
                </Grid>
                
                {!createData.auto_start && (
                  <Grid item xs={12}>
                    <DateTimePicker
                      label="Scheduled Start Time (GMT+2)"
                      value={createData.start_time}
                      onChange={(newValue) => setCreateData(prev => ({ ...prev, start_time: newValue }))}
                      ampm={false}
                      format="MM/dd/yyyy HH:mm"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: "Select when the auction should start automatically (24-hour format, GMT+2)"
                        }
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAuction}
              variant="contained"
              disabled={!createData.auction_name || selectedLotIds.length === 0}
            >
              Create Auction
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Auction Dialog */}
        <Dialog 
          open={editDialog} 
          onClose={() => setEditDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Auction</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Auction Name"
                    value={editData.auction_name}
                    onChange={(e) => setEditData(prev => ({ ...prev, auction_name: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={editData.description}
                    onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Auction Type"
                    value={editData.auction_type}
                    onChange={(e) => setEditData(prev => ({ ...prev, auction_type: e.target.value as 'normal' | 'dutch' }))}
                    fullWidth
                  >
                    <MenuItem value="normal">Normal Auction (Price increases)</MenuItem>
                    <MenuItem value="dutch">Dutch Auction (Price decreases)</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Reserve Price ($)"
                    type="number"
                    value={editData.reserve_price}
                    onChange={(e) => setEditData(prev => ({ ...prev, reserve_price: Number(e.target.value) }))}
                    fullWidth
                    helperText="Minimum acceptable price"
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ color: 'text.secondary', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Starting Price ($)"
                    type="number"
                    value={editData.starting_price}
                    onChange={(e) => setEditData(prev => ({ ...prev, starting_price: Number(e.target.value) }))}
                    fullWidth
                    helperText="Initial auction price (should be >= reserve price)"
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ color: 'text.secondary', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Duration (minutes)"
                    type="number"
                    value={editData.duration_minutes}
                    onChange={(e) => setEditData(prev => ({ ...prev, duration_minutes: Number(e.target.value) }))}
                    fullWidth
                    helperText="e.g., 10 for 10 minutes, 60 for 1 hour, 1440 for 24 hours"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <DateTimePicker
                    label="Start Time (GMT+2)"
                    value={editData.start_time}
                    onChange={(newValue) => {
                      console.log('DateTimePicker onChange - New value:', newValue);
                      console.log('DateTimePicker onChange - Local string:', newValue?.toString());
                      console.log('DateTimePicker onChange - ISO string:', newValue?.toISOString());
                      setEditData(prev => ({ ...prev, start_time: newValue }));
                    }}
                    ampm={false}
                    format="MM/dd/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: "Select when the auction should start (24-hour format, GMT+2)"
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditAuction}
              variant="contained"
              disabled={!editData.auction_name}
            >
              Update Auction
            </Button>
          </DialogActions>
        </Dialog>

        {/* Select Lots Dialog */}
        <Dialog 
          open={addLotDialog} 
          onClose={() => setAddLotDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Select Lots from Farmers</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {loadingLots ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : availableLots.length === 0 ? (
                <Alert severity="info">
                  No available lots found. Farmers need to create lots first.
                </Alert>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Lot Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Farmer</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Reserve Price</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableLots.map((lot) => (
                        <TableRow 
                          key={lot.id}
                          hover
                          onClick={() => handleSelectLot(lot)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLotIds.includes(lot.id)}
                              onChange={(e) => {
                                e.stopPropagation()
                                handleSelectLot(lot)
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">{lot.lot_name}</Typography>
                              {lot.quality_grade && (
                                <Typography variant="caption" color="text.secondary">
                                  Grade: {lot.quality_grade}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {lot.lot_description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{lot.farmer_name || 'Unknown'}</TableCell>
                          <TableCell>{lot.quantity} {lot.unit}</TableCell>
                          <TableCell>${lot.reserve_price}</TableCell>
                          <TableCell>
                            <Chip label={lot.status} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {selectedLotIds.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="primary.contrastText">
                    <strong>{selectedLotIds.length} lot(s) selected</strong>
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddLotDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => setAddLotDialog(false)}
              variant="contained"
              disabled={selectedLotIds.length === 0}
            >
              Confirm Selection
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action Dialog */}
        <Dialog 
          open={actionDialog} 
          onClose={() => setActionDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {actionType === 'start' && 'Start Auction'}
            {actionType === 'stop' && 'Stop Auction'}
            {actionType === 'cancel' && 'Cancel Auction'}
            {actionType === 'delete' && 'Delete Auction'}
            {actionType === 'enable' && 'Enable Auction'}
            {actionType === 'disable' && 'Disable Auction'}
          </DialogTitle>
          <DialogContent>
            <Alert severity={actionType === 'cancel' || actionType === 'delete' ? 'warning' : 'info'} sx={{ mt: 2 }}>
              {actionType === 'start' && 'This will make the auction live and allow bidding to begin.'}
              {actionType === 'stop' && 'This will end the auction and determine the winner.'}
              {actionType === 'cancel' && 'This will cancel the auction and notify all participants.'}
              {actionType === 'delete' && 'This will permanently delete the auction. This action cannot be undone.'}
              {actionType === 'enable' && 'This will activate the auction and allow bidding.'}
              {actionType === 'disable' && 'This will deactivate the auction and stop bidding.'}
            </Alert>
            
            {selectedAuction && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Auction:</strong> {selectedAuction.lot_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Product:</strong> {selectedAuction.product_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Status:</strong> {selectedAuction.status}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAuctionAction}
              variant="contained"
              color={actionType === 'cancel' || actionType === 'delete' ? 'error' : 'primary'}
            >
              {actionType === 'start' && 'Start Auction'}
              {actionType === 'stop' && 'Stop Auction'}
              {actionType === 'cancel' && 'Cancel Auction'}
              {actionType === 'delete' && 'Delete Auction'}
              {actionType === 'enable' && 'Enable Auction'}
              {actionType === 'disable' && 'Disable Auction'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Auction Dialog */}
        <Dialog 
          open={viewAuctionDialog} 
          onClose={() => setViewAuctionDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {selectedAuction?.lot_name || 'Auction Details'}
              </Typography>
              <IconButton onClick={() => setViewAuctionDialog(false)} size="small">
                <Delete />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedAuction && (
              <Box>
                {/* Auction Images */}
                {((selectedAuction.lot_images?.length || 0) > 0 || (selectedAuction.harvest_images?.length || 0) > 0 || (selectedAuction.all_lot_images?.length || 0) > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Auction Images
                    </Typography>
                    <Grid container spacing={2}>
                      {(selectedAuction.lot_images || selectedAuction.harvest_images || selectedAuction.all_lot_images || []).map((img: any, index: number) => {
                        const imageUrl = typeof img === 'string' ? img : img.url
                        const fullUrl = imageUrl?.startsWith('http') ? imageUrl : `http://localhost:3000${imageUrl}`
                        return (
                          <Grid item xs={6} sm={4} md={3} key={index}>
                            <Box
                              component="img"
                              src={fullUrl}
                              alt={`Auction image ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 150,
                                objectFit: 'cover',
                                borderRadius: 1,
                                border: '1px solid #ddd',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 }
                              }}
                              onClick={() => handleOpenImageOverlay(selectedAuction!, index)}
                              onError={(e: any) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E'
                              }}
                            />
                          </Grid>
                        )
                      })}
                    </Grid>
                  </Box>
                )}

                {/* Auction Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Auction Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Auction Name</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedAuction.lot_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Product</Typography>
                      <Typography variant="body1">{selectedAuction.product_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Farmer</Typography>
                      <Typography variant="body1">{selectedAuction.farmer_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Auction Type</Typography>
                      <Chip label={selectedAuction.auction_type} size="small" color={selectedAuction.auction_type === 'normal' ? 'primary' : 'secondary'} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip label={selectedAuction.status} color={getStatusColor(selectedAuction.status) as any} size="small" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">{selectedAuction.location}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Bidding Information */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Bidding Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Current Bid</Typography>
                      <Typography variant="h6" color="success.main">${selectedAuction.current_bid}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Reserve Price</Typography>
                      <Typography variant="body1">${selectedAuction.reserve_price}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Number of Bids</Typography>
                      <Typography variant="body1">{selectedAuction.bid_count}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Winner</Typography>
                      <Typography variant="body1">{selectedAuction.winner_name || 'No winner yet'}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Timing Information */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Timing Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Start Time</Typography>
                      <Typography variant="body1">{new Date(selectedAuction.start_time).toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">End Time</Typography>
                      <Typography variant="body1">{new Date(selectedAuction.end_time).toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Created Date</Typography>
                      <Typography variant="body1">{new Date(selectedAuction.created_date).toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewAuctionDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Overlay */}
        <ImageOverlay
          open={imageOverlayOpen}
          onClose={() => setImageOverlayOpen(false)}
          images={overlayImages}
          currentImageIndex={currentImageIndex}
          onImageChange={setCurrentImageIndex}
        />

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  )
}

export default Auctions
